import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const AdminModalTailwind = ({ 
  isOpen, 
  onClose, 
  title, 
  subtitle, 
  children, 
  onSave, 
  onCancel,
  saveButtonText,
  cancelButtonText,
  isLoading = false,
  isSaving = false,
  isSaveDisabled = false
}) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);

  const safeClose = useCallback(() => {
    if (!isSaving) onClose();
  }, [isSaving, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      safeClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        safeClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, safeClose]);

  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    panelRef.current.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (onSave && !isSaving) {
      onSave();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const showFooter = saveButtonText != null || cancelButtonText != null;

  return (
    <div 
      className="fixed inset-0 z-[100] overflow-y-auto min-h-[100dvh]"
      role="presentation"
    >
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-modal-title"
          className="relative w-full max-w-4xl transform rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg transition-all outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:focus-visible:ring-primary-light/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-800"
        >
          <div className="border-b border-slate-200 dark:border-slate-700 px-5 sm:px-6 py-4 rounded-t-2xl">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2
                  id="admin-modal-title"
                  className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100"
                >
                  {title}
                </h2>
                {subtitle && (
                  <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {subtitle}
                  </p>
                )}
              </div>
              <button 
                type="button"
                className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-40 disabled:pointer-events-none"
                onClick={safeClose}
                disabled={isSaving}
                title={t("admin.closeModal", "Schließen")}
                aria-label={t("admin.closeModal", "Schließen")}
              >
                <i className="fas fa-times text-sm" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className={`px-5 sm:px-6 py-5 sm:py-6 bg-white dark:bg-slate-800 overflow-visible ${!showFooter ? "rounded-b-2xl" : ""}`}>
            {children}
          </div>

          {showFooter && (
            <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-700/40 px-5 sm:px-6 py-4 rounded-b-2xl">
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
                {cancelButtonText != null && (
                  <button 
                    type="button"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    {cancelButtonText}
                  </button>
                )}
                {saveButtonText != null && (
                  <button 
                    type="button"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    onClick={handleSave}
                    disabled={isSaving || isLoading || isSaveDisabled}
                  >
                    {isSaving ? (
                      <>
                        <i className="fas fa-spinner fa-spin text-xs" aria-hidden="true"></i>
                        {t("admin.saving", "Speichern...")}
                      </>
                    ) : (
                      saveButtonText
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminModalTailwind;
